<script setup lang="ts">
import { computed, ref, watch } from 'vue'

interface Props {
  dateFrom: string | null
  dateTo: string | null
  active?: boolean
}

interface Emits {
  (e: 'apply', from: string | null, to: string | null): void
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
})

const emit = defineEmits<Emits>()

const isOpen = ref(false)
const localDateFrom = ref<string>(props.dateFrom || '')
const localDateTo = ref<string>(props.dateTo || '')
const dateFromMenu = ref(false)
const dateToMenu = ref(false)

const filterIcon = computed(() => {
  return props.active ? 'ri-filter-fill' : 'ri-filter-2-line'
})

const isApplyDisabled = computed(() => {
  return !(localDateFrom.value || localDateTo.value)
})

watch(() => props.dateFrom, newVal => {
  localDateFrom.value = newVal || ''
})

watch(() => props.dateTo, newVal => {
  localDateTo.value = newVal || ''
})

const datePresets = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'current_week', label: 'Текущая неделя' },
  { id: 'last_week', label: 'Прошлая неделя' },
  { id: 'current_month', label: 'Текущий месяц' },
  { id: 'last_month', label: 'Прошлый месяц' },
]

/** YYYY-MM-DD для API */
const formatDateForApi = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** ДД.ММ.ГГГГ для поля ввода */
const formatDateForDisplay = (value: string): string => {
  const date = parseLocalDate(value)
  if (!date) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${date.getFullYear()}`
}

const parseLocalDate = (value: string): Date | undefined => {
  if (!value) return undefined
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [day, month, year] = value.split('.').map(Number)
    return new Date(year, month - 1, day)
  }
  const isoDate = value.split('T')[0]
  const [year, month, day] = isoDate.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

const pickerValueToApi = (val: unknown): string => {
  if (!val) return ''
  if (val instanceof Date) return formatDateForApi(val)
  if (typeof val === 'string') {
    const date = parseLocalDate(val)
    return date ? formatDateForApi(date) : ''
  }
  return ''
}

const setDatePreset = (preset: string) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const getStartOfWeekMonday = (date: Date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diffToMonday = (day === 0 ? -6 : 1) - day
    d.setDate(d.getDate() + diffToMonday)
    return d
  }

  const addDays = (date: Date, n: number) => {
    const d = new Date(date)
    d.setDate(d.getDate() + n)
    return d
  }

  const getLastDayOfMonth = (year: number, monthIndex: number) => {
    return new Date(year, monthIndex + 1, 0)
  }

  switch (preset) {
    case 'today':
      localDateFrom.value = formatDateForApi(today)
      localDateTo.value = formatDateForApi(today)
      break
    case 'yesterday':
      {
        const y = addDays(today, -1)
        localDateFrom.value = formatDateForApi(y)
        localDateTo.value = formatDateForApi(y)
      }
      break
    case 'current_week':
      {
        const startOfWeek = getStartOfWeekMonday(today)
        const endOfWeek = addDays(startOfWeek, 6)
        localDateFrom.value = formatDateForApi(startOfWeek)
        localDateTo.value = formatDateForApi(endOfWeek)
      }
      break
    case 'last_week':
      {
        const startOfThisWeek = getStartOfWeekMonday(today)
        const startOfLastWeek = addDays(startOfThisWeek, -7)
        const endOfLastWeek = addDays(startOfLastWeek, 6)
        localDateFrom.value = formatDateForApi(startOfLastWeek)
        localDateTo.value = formatDateForApi(endOfLastWeek)
      }
      break
    case 'current_month':
      {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const lastDay = getLastDayOfMonth(today.getFullYear(), today.getMonth())
        localDateFrom.value = formatDateForApi(startOfMonth)
        localDateTo.value = formatDateForApi(lastDay)
      }
      break
    case 'last_month':
      {
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const lastDayLastMonth = getLastDayOfMonth(startOfLastMonth.getFullYear(), startOfLastMonth.getMonth())
        localDateFrom.value = formatDateForApi(startOfLastMonth)
        localDateTo.value = formatDateForApi(lastDayLastMonth)
      }
      break
  }
}

const onDateFromSelect = (val: unknown) => {
  const next = pickerValueToApi(val)
  if (next) localDateFrom.value = next
  dateFromMenu.value = false
}

const onDateToSelect = (val: unknown) => {
  const next = pickerValueToApi(val)
  if (next) localDateTo.value = next
  dateToMenu.value = false
}

const handleApply = () => {
  emit('apply', localDateFrom.value || null, localDateTo.value || null)
  isOpen.value = false
}
</script>

<template>
  <VMenu
    v-model="isOpen"
    :close-on-content-click="false"
    location="bottom"
    transition="scale-transition"
    :max-width="220"
  >
    <template #activator="{ props: menuProps }">
      <VIconBtn
        v-bind="menuProps"
        variant="plain"
        size="small"
        :icon="filterIcon"
        :color="active ? 'primary' : undefined"
      />
    </template>

    <VCard class="pa-4">
      <VMenu
        v-model="dateFromMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        location="bottom start"
        min-width="auto"
      >
        <template #activator="{ props: activatorProps }">
          <VTextField
            v-bind="activatorProps"
            :model-value="formatDateForDisplay(localDateFrom)"
            variant="outlined"
            label="дата с"
            density="compact"
            class="mb-3"
            append-inner-icon="ri-calendar-line"
            hide-details
            readonly
            @click="dateFromMenu = true"
            @click:append-inner.prevent.stop="dateFromMenu = true"
          />
        </template>

        <VDatePicker
          :model-value="parseLocalDate(localDateFrom)"
          weekday-format="short"
          hide-header
          @update:model-value="onDateFromSelect"
        />
      </VMenu>

      <VMenu
        v-model="dateToMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        location="bottom start"
        min-width="auto"
      >
        <template #activator="{ props: activatorProps }">
          <VTextField
            v-bind="activatorProps"
            :model-value="formatDateForDisplay(localDateTo)"
            variant="outlined"
            label="дата по"
            density="compact"
            class="mb-4"
            append-inner-icon="ri-calendar-line"
            hide-details
            readonly
            @click="dateToMenu = true"
            @click:append-inner.prevent.stop="dateToMenu = true"
          />
        </template>

        <VDatePicker
          :model-value="parseLocalDate(localDateTo)"
          weekday-format="short"
          hide-header
          @update:model-value="onDateToSelect"
        />
      </VMenu>

      <div class="d-flex flex-wrap gap-2 mb-4">
        <VChip
          v-for="preset in datePresets"
          :key="preset.id"
          size="small"
          @click="setDatePreset(preset.id)"
        >
          {{ preset.label }}
        </VChip>
      </div>

      <VBtn
        variant="elevated"
        size="small"
        block
        :disabled="isApplyDisabled"
        @click="handleApply"
      >
        Применить
      </VBtn>
    </VCard>
  </VMenu>
</template>
